'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { IconCheck, IconChevronDown } from '@icons';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  placeholder?: boolean;
};

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  className?: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  hintClassName?: string;
  hintId?: string;
  hintHidden?: boolean;
  options: SelectOption[];
}

type SelectValue = string | number | readonly string[] | undefined;

const readValues = (raw: SelectValue): string[] => {
  if (Array.isArray(raw)) return raw.map(String).filter((item) => item !== '');
  if (raw == null || raw === '') return [];
  return [String(raw)];
};

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className = '',
      options,
      label,
      hint,
      hintClassName,
      hintId,
      hintHidden,
      id,
      name,
      value,
      defaultValue,
      onChange,
      multiple,
      required,
      disabled,
      autoComplete,
      form,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-errormessage': ariaErrorMessage,
      'aria-required': ariaRequired,
      ...selectProps
    },
    ref
  ) => {
    const isMultiple = Boolean(multiple);
    const reactId = useId();
    const fieldId = id ?? reactId;
    const listId = `${fieldId}-list`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const isControlled = value !== undefined;
    const [uncontrolledValues, setUncontrolledValues] = useState(() => readValues(defaultValue));
    const selectedValues = isControlled ? readValues(value) : uncontrolledValues;
    const [open, setOpen] = useState(false);
    const choices = options.filter((option) => !option.placeholder);
    const selectedIndex = choices.findIndex((option) => selectedValues.includes(option.value));
    const [active, setActive] = useState(selectedIndex >= 0 ? selectedIndex : 0);
    const placeholderOption = options.find((option) => option.placeholder);
    const selectedLabels = choices
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label);
    const triggerLabel = selectedLabels.length
      ? selectedLabels.join(', ')
      : (placeholderOption?.label ?? '');

    useEffect(() => {
      if (!open) return;
      const onDocMouse = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (target && rootRef.current && !rootRef.current.contains(target)) setOpen(false);
      };
      document.addEventListener('mousedown', onDocMouse);
      return () => document.removeEventListener('mousedown', onDocMouse);
    }, [open]);

    const emit = (next: string[]) => {
      if (!isControlled) setUncontrolledValues(next);
      onChange?.({
        target: { value: isMultiple ? next.join(',') : (next[0] ?? ''), name: name ?? '' },
      } as React.ChangeEvent<HTMLSelectElement>);
    };

    const onNativeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = isMultiple
        ? Array.from(event.target.selectedOptions, (option) => option.value)
        : event.target.value
          ? [event.target.value]
          : [];
      emit(next);
    };

    const choose = (option: SelectOption) => {
      if (option.disabled || option.placeholder) return;
      if (!isMultiple) {
        emit([option.value]);
        setOpen(false);
        return;
      }
      const next = selectedValues.includes(option.value)
        ? selectedValues.filter((item) => item !== option.value)
        : [...selectedValues, option.value];
      emit(next);
    };

    const moveActive = (delta: number) => {
      if (!choices.length) return;
      setActive((index) => {
        let next = index;
        for (let step = 0; step < choices.length; step += 1) {
          next = (next + delta + choices.length) % choices.length;
          if (!choices[next]?.disabled) return next;
        }
        return index;
      });
    };

    const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) {
          setActive(selectedIndex >= 0 ? selectedIndex : 0);
          setOpen(true);
          return;
        }
        moveActive(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        if (!open) return;
        event.preventDefault();
        setActive(event.key === 'Home' ? 0 : choices.length - 1);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!open) {
          setActive(selectedIndex >= 0 ? selectedIndex : 0);
          setOpen(true);
          return;
        }
        const option = choices[active];
        if (option) choose(option);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };

    return (
      <>
        {label ? <label htmlFor={fieldId}>{label}</label> : null}
        <div className={`input-affix input-select${open ? ' open' : ''}`} ref={rootRef}>
          <select
            {...selectProps}
            name={name}
            ref={ref}
            className='sr-only'
            aria-hidden
            tabIndex={-1}
            required={required}
            disabled={disabled}
            autoComplete={autoComplete}
            form={form}
            multiple={isMultiple}
            value={isMultiple ? selectedValues : (selectedValues[0] ?? '')}
            onChange={onNativeChange}
          >
            {options.map((option, idx) => {
              const isPlaceholder = option.placeholder ?? false;
              const isDisabled = Boolean(option.disabled) || isPlaceholder;
              return (
                <option
                  key={`${option.value}-${idx}`}
                  value={option.value}
                  disabled={isDisabled}
                  hidden={isPlaceholder}
                >
                  {option.label}
                </option>
              );
            })}
          </select>
          <button
            type='button'
            id={fieldId}
            className={`select-trigger${selectedLabels.length ? '' : ' is-placeholder'}${
              className ? ` ${className}` : ''
            }`}
            value={selectedValues.join(',')}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-errormessage={ariaErrorMessage}
            aria-required={ariaRequired}
            aria-haspopup='listbox'
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={open ? `${fieldId}-opt-${active}` : undefined}
            disabled={disabled}
            onClick={() => {
              if (!open) setActive(selectedIndex >= 0 ? selectedIndex : 0);
              setOpen((isOpen) => !isOpen);
            }}
            onKeyDown={onTriggerKeyDown}
          >
            {triggerLabel}
          </button>
          <span className='affix-icon' aria-hidden='true'>
            <IconChevronDown />
          </span>
          {open ? (
            <ul
              className='combo-list select-menu'
              id={listId}
              role='listbox'
              aria-multiselectable={isMultiple || undefined}
            >
              {choices.map((option, index) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <li
                    key={`${option.value}-${index}`}
                    id={`${fieldId}-opt-${index}`}
                    className={`combo-item${index === active ? ' is-active' : ''}${
                      isSelected ? ' is-selected' : ''
                    }`}
                    role='option'
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    onMouseEnter={() => {
                      if (!option.disabled) setActive(index);
                    }}
                    onClick={() => choose(option)}
                  >
                    <span>{option.label}</span>
                    {isMultiple && isSelected ? <IconCheck /> : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        {hint ? (
          <small
            id={hintId}
            hidden={hintHidden}
            className={`hint${hintClassName ? ' ' + hintClassName : ''}`}
          >
            {hint}
          </small>
        ) : null}
      </>
    );
  }
);

Select.displayName = 'Select';

export default Select;
